import { useMemo } from 'react';

import { PintoEbookCollectionQuery, PintoEbookCollectionStatus } from '../../../core/graphql/types';
import { getDayTime } from '../../../utils/dayjs';
import { PINTO_BOOK_BASE_URL } from '../../ebooks/presenters/AboutDetailPresenter';
import { KOLColorPaletteCard } from '../components/KOLColorPaletteCard';

const DATE_FORMAT = 'DD-MM-YYYY hh:mm A';

export interface IKOLDetailPresenterProps {
  collectionData: NonNullable<PintoEbookCollectionQuery['pintoEbookCollection']>;
}

function KOLDetailPresenter(props: IKOLDetailPresenterProps) {
  const { collectionData } = props;

  const statusBadgeClass = useMemo(() => {
    switch (collectionData.status) {
      case PintoEbookCollectionStatus.Public:
        return 'badge-success';
      case PintoEbookCollectionStatus.Draft:
        return 'badge-warning';
      case PintoEbookCollectionStatus.Removed:
        return 'badge-error';
      default:
        return 'badge-outline';
    }
  }, [collectionData.status]);

  return (
    <div className="p-24 space-y-24 bg-componentsBgGrouped02 rounded-lg">
      <div className="flex items-center justify-between">
        <h2 className="font-dbh text-[20px]">Collection Details</h2>
        <div className={`badge ${statusBadgeClass}`}>{collectionData.status?.toLocaleUpperCase() || 'UNKNOWN'}</div>
      </div>

      <div className="grid grid-cols-1 gap-16 md:grid-cols-2">
        <div className="space-y-12">
          <div>
            <label className="text-sm font-medium text-colorsBrandWarmBlack02LabelSecondary">Collection ID</label>
            <a target="_blank" className="block" href={`${PINTO_BOOK_BASE_URL}/ebook-collections/${collectionData._id}`} rel="noreferrer">
              <p className="font-tt text-colorsBrandWarmBlack01LabelPrimary underline">{collectionData._id}</p>
            </a>
          </div>
          <div>
            <label className="text-sm font-medium text-colorsBrandWarmBlack02LabelSecondary">Title</label>
            <p className="font-tt text-colorsBrandWarmBlack01LabelPrimary break-words break-word">{collectionData.title}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-colorsBrandWarmBlack02LabelSecondary">Description</label>
            <p className="font-tt text-colorsBrandWarmBlack01LabelPrimary break-words break-word">{collectionData.description}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-colorsBrandWarmBlack02LabelSecondary">Emoji</label>
            <p className="font-tt text-colorsBrandWarmBlack01LabelPrimary text-2xl">{collectionData.emoji}</p>
          </div>
        </div>

        <div className="space-y-12">
          <div>
            <label className="text-sm font-medium text-colorsBrandWarmBlack02LabelSecondary">Item Count</label>
            <p className="font-tt text-colorsBrandWarmBlack01LabelPrimary">{collectionData.itemCount} items</p>
          </div>
          <div>
            <label className="text-sm font-medium text-colorsBrandWarmBlack02LabelSecondary">Created At</label>
            <p className="font-tt text-colorsBrandWarmBlack01LabelPrimary">{getDayTime(collectionData.createdAt, DATE_FORMAT)}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-colorsBrandWarmBlack02LabelSecondary">Updated At</label>
            <p className="font-tt text-colorsBrandWarmBlack01LabelPrimary">{getDayTime(collectionData.updatedAt, DATE_FORMAT)}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-colorsBrandWarmBlack02LabelSecondary">Color Palette (Light & Dark)</label>
            <div className="flex flex-wrap gap-x-24 gap-y-6 mt-4">
              <KOLColorPaletteCard lightColor={collectionData.hexColorCode} darkColor={collectionData.darkModeHexColorCode} isSelected={false} onClick={() => undefined} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default KOLDetailPresenter;
